import { swSettings } from "app/common/services/swSettings";
import type { IAsset } from "pro-features/components/AutocompleteWithTabs/types";
import type { ICampaignsDataUnion } from "app/components/campaigns/shared/CampaignsConst";
import { campaignsFormatNb } from "app/components/campaigns/shared/CampaignsUtils";
import { withCampaignDatesUtc } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import { translate } from "app/filters/i18nFilter";
import { fetchService } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordsCampaignData,
    IKeywordsCampaigns,
    IKeywordsCampaignsCampaignRawData,
    IMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { TabFilter } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { getSingleCampaignURL } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import { NoCacheHeaders } from "app/services/fetchService";
import { RecentService } from "app/services/recent/recentService";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import { isInValidDomainWithoutSubFolder } from "../wizard/wizard-pages/create-project-step/adding-competitors/utils";

export const getIsRTCreateCampaignsAvailable = (): boolean => {
    return swSettings.components.PerformanceAddOn.resources.NumberTrackedKeywords > 0;
};

export const getAPIPrefixForRT = (isDemoCampaign: boolean): string => {
    return !isDemoCampaign ? "api/rankTracker" : "api/rankTracker/demo";
};

const getCampaignsURL = `/campaigns`;
export const fetchCampaigns = async (): Promise<IKeywordsCampaignsCampaignRawData[]> => {
    const getOnlyDemoCampaigns = !getIsRTCreateCampaignsAvailable();
    const campaignConfigs: IKeywordsCampaigns = await fetchService.get(
        `${getAPIPrefixForRT(getOnlyDemoCampaigns)}${getCampaignsURL}`,
        {},
        {
            headers: NoCacheHeaders,
            preventAutoCancellation: true, //requires to prevent auto cancellation on navigation change during the fetch
        },
    );
    const userCampaigns =
        campaignConfigs?.userCampaigns?.map((campaign) => ({
            ...withCampaignDatesUtc(campaign),
            isShared: false,
            isEditable: true,
        })) ?? [];

    const sharedCampaigns =
        campaignConfigs?.sharedCampaigns?.map((campaign) => ({
            ...withCampaignDatesUtc(campaign),
            isShared: true,
        })) ?? [];

    return [...userCampaigns, ...sharedCampaigns];
};

export const deleteCampaigns = async (campaignID: string): Promise<void> => {
    return fetchService.delete(`${getSingleCampaignURL(false)}/${campaignID}`);
};

export const doesCampaignMatchFilter = (campaign: IKeywordsCampaignData, tabIndex: number) =>
    ({
        [TabFilter.ALL]: !campaign.isDemo,
        [TabFilter.MY]: !campaign.isShared && !campaign.isDemo,
        [TabFilter.EDITABLE]: campaign.isEditable,
        [TabFilter.SHARED]: campaign.isShared,
        [TabFilter.DEMO]: campaign.isDemo,
    })[tabIndex] || false;

export const isCampaignActive = (campaign: IKeywordsCampaignData): boolean => !!campaign?.firstScrapeTime;

export const getForbiddenSitesForCampaignsFromRecents = (): IAsset[] => {
    const recentSearches = RecentService.getRecentsWebsites();
    return recentSearches
        .filter((item) => isInValidDomainWithoutSubFolder(item.data.mainItem))
        .map((item) => {
            return {
                displayText: item.data.mainItem,
                image: null,
                type: EAssetType.Website,
                id: item.data.mainItem,
                name: item.data.mainItem,
            };
        });
};

export const getMyUserConsumptionMetrics = (
    campaignsConfigurations: ICampaignsDataUnion,
): IMyUserConsumptionMetrics => {
    const myCampaignsIndex = campaignsConfigurations?.findIndex((campaign) => campaign.isShared);
    if (myCampaignsIndex === -1) {
        return {
            usedKeywords: 0,
            usedCampaigns: 0,
        };
    }
    const myCampaigns = campaignsConfigurations?.slice(0, myCampaignsIndex);
    const amountOfKeywords = myCampaigns?.reduce((acc, campaign) => {
        return acc + campaign.keywordCount;
    }, 0);
    return {
        usedKeywords: amountOfKeywords,
        usedCampaigns: myCampaigns?.length,
    };
};

export const getRankTrackerCampaignsAndKeywordsSubtitle = (
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics,
    myUserConsumptionMetrics: IMyUserConsumptionMetrics,
): string => {
    if (swSettings.user.isInternalUser) {
        return translate("seo.keywordsCampaign.campaigns.banner.balance.subTitle.internalUser", {
            usedCampaigns: campaignsFormatNb(myUserConsumptionMetrics?.usedCampaigns),
            usedKeywords: campaignsFormatNb(myUserConsumptionMetrics?.usedKeywords),
            maxKeywords: swSettings.components.RankTracker.resources.MaxKeywordInternalRankTracker,
        });
    }
    return translate("seo.keywordsCampaign.campaigns.banner.balance.subTitle", {
        usedCampaigns: campaignsFormatNb(campaignsConsumptionMetrics.usedCampaigns),
        totalCampaigns: campaignsFormatNb(campaignsConsumptionMetrics.totalCampaigns),
        usedKeywords: campaignsFormatNb(campaignsConsumptionMetrics.usedKeywords),
        totalKeywords: campaignsFormatNb(campaignsConsumptionMetrics.totalKeywords),
    });
};
